import { PolymerElement, html } from '@polymer/polymer';
import { GestureEventListeners } from '@polymer/polymer/lib/mixins/gesture-event-listeners.js';
import { setPassiveTouchGestures } from '@polymer/polymer/lib/utils/settings.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import '@wdpr/typography';
import '@wdpr/color';
import '@wdpr/mdx-icons';
import './style/wdpr-checkbox-style.js';
// Configure passive event listeners before any event listeners are attached.
setPassiveTouchGestures(true);
class WdprCheckbox extends GestureEventListeners(PolymerElement) {
  static get is() {
    return 'wdpr-checkbox';
  }
  static get template() {
    return html`
        <!-- Scoped styles for this element -->
        <style include="wdpr-typography"></style>
        <style include="wdpr-color"></style>
        <style include="wdpr-mdx-icons"></style>
        <style include="wdpr-checkbox-style"></style>
        <label id="wrapper"
            for\$="[[inputId]]"
            on-tap="_labelTapped">
            <input type="checkbox"
                id\$="[[inputId]]"
                error\$=[[error]]
                disabled\$="[[disabled]]"
                checked\$="{{checked::change}}"
                aria-checked\$="[[ariaChecked]]"
                aria-labelledby\$="[[getAriaLabel(customAriaLabel, inputId)]]"
                aria-describedby\$="custom-error-aria-label-[[inputId]]"
                tabindex\$="{{tabindex}}" />
            <template is="dom-if" if="[[!imagePath]]">
                <span id="checkmark"
                    aria-hidden="true">
                    <i id="checkboxIcon" class\$="[[fontClass]]"></i>
                </span>
            </template>
            <template is="dom-if" if="[[imagePath]]">
                <img id="checkboxImage"
                    src="[[displayedImage]]"
                    alt="[[displayedImage]]"
                    aria-hidden="true" />
            </template>
            <div id\$="default-aria-label-[[inputId]]" class="checkbox-label" aria-hidden="true">
                <slot id="slot"></slot>
            </div>
            <!-- Hidden span with the custom aria-label for a11y -->
            <span id\$="custom-aria-label-[[inputId]]" hidden>[[customAriaLabel]]</span>
            <span id\$="custom-error-aria-label-[[inputId]]" hidden>[[computeCustomErrorAriaLabel(error, customErrorAriaLabel)]]</span>
        </label>
        `;
  }
  static get properties() {
    return {
      alignment: {
        type: String,
        value: 'center',
        reflectToAttribute: true
      },
      checked: {
        type: Boolean,
        notify: true,
        reflectToAttribute: true,
        observer: '_onChecked'
      },
      error: {
        type: Boolean,
        value: false
      },
      disabled: {
        type: Boolean,
        value: false
      },
      inputId: {
        type: String,
        computed: '_generateRandId()'
      },
      /* 
      NOTE: the aria-checked role must be explicitly defined due to a defect
      with Polymer, Screen Readers, and the Native HTML input type checkbox.
      */
      ariaChecked: {
        type: String,
        value: 'false'
      },
      customTabindex: {
        type: Number
      },
      tabindex: {
        type: Number,
        computed: '_computeTabIndex(disabled, customTabindex)'
      },
      imagePath: {
        type: String,
        value: ''
      },
      imageExtension: {
        type: String,
        value: 'svg'
      },
      fontClass: {
        type: String,
        value: ''
      },
      displayedImage: {
        type: String,
        computed: '_computedImageName(imagePath, imageExtension, checked, disabled, error)'
      },
      customAriaLabel: {
        type: String,
        value: ''
      },
      customErrorAriaLabel: {
        type: String,
        value: ''
      }
    };
  }

  ready() {
    super.ready();
    this.ariaChecked = this.checked ? 'true' : 'false';
    // For A11Y, only highlight the element when focused if the user is using key presses
    // to navigate between elements on the page.
    this.addEventListener('mousedown', this._setMouseDownGesture.bind(this));
    this.addEventListener('keydown', this._setKeydownGesture.bind(this));
  }
  /**
   * @desc Handle mouse-navigation attribute.
   */
  _setMouseDownGesture() {
    this.setAttribute('mouse-navigation', '');
  }
  /**
   * @desc Handle space or enter key press to toggle the checked state of the input.
   */
  _setKeydownGesture(e) {
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault();
      if (this.disabled) {
        return;
      }
      this.checked = !this.checked;
    } else {
      this.removeAttribute('mouse-navigation');
    }
  }
  _labelTapped(event) {
    event.preventDefault();
    this.checked = !this.checked;
  }
  /**
   * @desc Generate a random ID for a11y. Added this method to fix linting errors
   * @return {String}
   */
  _generateRandId() {
    const length = 6;
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let result = '';
    for (let i = length; i > 0; --i) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }
  /**
   * @desc Set internal values to toggle the component
   */
  _onChecked() {
    this.fontClass = this.checked ? 'di di-checkmark' : '';
    this.ariaChecked = this.checked ? 'true' : 'false';
  }

  /**
   * @desc Determine the tabindex based on the disabled state of the checkbox
   * @param disabled the "disable" property of the component
   * @param customTabindex custom tabindex to be used instead of disabled
   * @return customTabindex if exists, otherwise 0 if the checkbox is enabled, -1 if it's disabled.
   */
  _computeTabIndex(disabled, customTabindex) {
    if (customTabindex) {
      return customTabindex;
    } else {
      return disabled ? -1 : 0;
    }
  }
  /**
   * @desc Set image for the checkbox
   * @param {String} imagePath
   * @param {String} imageExtension
   * @param {Boolean} checked
   * @param {Boolean} isDisabled
   * @param {Boolean} error
   * @return {String}
   */
  _computedImageName(imagePath, imageExtension, checked, disabled, error) {
    if (!imagePath) {
      return;
    }
    let className = this._computeElementState(checked, disabled, error);
    if (className) {
      className = className.substring(1, className.length).replace(/\s+/g, '-');
    }
    if (!checked) {
      return className
        ? `${imagePath}kng-unchecked-${className}.${imageExtension}`
        : `${imagePath}kng-unchecked.${imageExtension}`;
    } else {
      return `${imagePath}kng-${className}.${imageExtension}`;
    }
  }
  /**
   * @desc Set class for the element state or behavior
   * @param {Boolean} checked
   * @param {Boolean} disabled
   * @param {Boolean} error
   * @return {String} className
   */
  _computeElementState(checked, disabled, error) {
    let className = '';
    if (checked) {
      className += ' checked';
    }
    if (disabled) {
      className += ' disabled';
    }
    if (error) {
      className += ' error';
    }
    return className;
  }
  /**
   * @desc Get id for aria-label
   * @param {String} customAriaLabel
   * @param {String} inputId
   * @return {String} aria-label id
   */
  getAriaLabel(customAriaLabel, inputId) {
    return `${
      customAriaLabel ? 'custom-aria-label-' : 'default-aria-label-'
      }${inputId}`;
  }
  /**
   * Get error for aria-label when it is available
   * @param {Boolean} error
   * @param {String} customErrorAriaLabel
   */
  computeCustomErrorAriaLabel(error, customErrorAriaLabel) {
    return (error && customErrorAriaLabel.trim() !== '') ? customErrorAriaLabel : '';
  }
}
window.customElements.get(WdprCheckbox.is) || window.customElements.define(WdprCheckbox.is, WdprCheckbox);